import React, { ReactNode } from "react";
import Pagination from "@amzn/awsui-components-console/pagination";
import Table from "@amzn/awsui-components-console/table";
import { Header, SpaceBetween } from "@amzn/awsui-components-console";
import TextFilter from "@amzn/awsui-components-console/text-filter";
import { useCollection } from "@amzn/awsui-collection-hooks";
import { PAGINATION_LABELS } from "../table/tableUtils";
import TableEmptyState from "../table/TableEmptyState";
import TableNoMatchState from "../table/TableNoMatchState";
import { TableProps } from "@amzn/awsui-components-console";

export interface SearchResultsProps<T> {
  /** Array of items to display in the table */
  items: T[];
  /** Column definitions for the table */
  columnDefinitions: TableProps.ColumnDefinition<T>[];
  /** Loading state */
  loading: boolean;
  /** Loading text to display */
  loadingText?: string;
  /** Resource name for empty state (e.g., "Claim", "Cargo") */
  resourceName: string;
  /** Header content */
  headerContent: ReactNode;
  /** Actions to display in the header */
  headerActions?: ReactNode;
  /** Search bar component */
  searchBarComponent?: ReactNode;
  /** Filter placeholder text */
  filterPlaceholder?: string;
  /** Filter aria label */
  filterAriaLabel?: string;
  /** Default sorting configuration */
  defaultSorting?: {
    sortingField: string;
    isDescending?: boolean;
  };
  /** Page size for pagination */
  pageSize?: number;
  /** Minimum items count to show filter and pagination */
  minItemsForControls?: number;
  /** Test ID for the component */
  testId?: string;
}

/**
 * Generic SearchResults component that can be used for different types of claims
 * @param props SearchResultsProps containing configuration for the table
 * @returns Generic search results table component
 */
function SearchResults<T>({
  items,
  columnDefinitions,
  loading,
  loadingText = "Loading...",
  resourceName,
  headerContent,
  headerActions,
  searchBarComponent,
  filterPlaceholder = `Filter ${resourceName}s`,
  filterAriaLabel = `Filter ${resourceName}s`,
  defaultSorting = { sortingField: "createdAtDate", isDescending: true },
  pageSize = 10,
  minItemsForControls = 5,
  testId = "search-results",
}: SearchResultsProps<T>) {
  const {
    items: collectionItems,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(items, {
    filtering: {
      empty: <TableEmptyState resourceName={resourceName} />,
      noMatch: <TableNoMatchState onClearFilter={() => actions.setFiltering("")} />,
    },
    pagination: { pageSize },
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: defaultSorting.sortingField },
        isDescending: defaultSorting.isDescending,
      },
    },
    selection: {},
  });

  const showControls = items && items.length >= minItemsForControls;

  return (
    <SpaceBetween direction="vertical" size="s" data-testid={testId}>
      {searchBarComponent}
      <Table
        {...collectionProps}
        items={collectionItems}
        columnDefinitions={columnDefinitions}
        variant="borderless"
        resizableColumns={false}
        wrapLines={true}
        stripedRows={true}
        header={<Header actions={headerActions}>{headerContent}</Header>}
        loading={loading}
        loadingText={loadingText}
        filter={
          showControls ? (
            <TextFilter
              {...filterProps}
              data-testid="text-filter"
              filteringAriaLabel={filterAriaLabel}
              filteringPlaceholder={filterPlaceholder}
              filteringClearAriaLabel="Clear"
              countText={
                filteredItemsCount
                  ? `${filteredItemsCount} ${filteredItemsCount === 1 ? "match" : "matches"}`
                  : ""
              }
            />
          ) : null
        }
        pagination={
          showControls ? (
            <Pagination
              {...paginationProps}
              data-testid="pagination"
              ariaLabels={PAGINATION_LABELS}
            />
          ) : null
        }
      />
    </SpaceBetween>
  );
}

export default SearchResults;
